import { queryStringParamsCache } from '../../app-kit/helpers/getCacheQueryStringParamsFromUrl';
import {
  SEARCH_BOUNDING_BOX_DEFAULT,
  SEARCH_COMMUTES_DEFAULT,
  SEARCH_EXCLUDE_DEPOSIT_TAKEN_DEFAULT,
  SEARCH_EXCLUDE_UNDER_OFFER_DEFAULT,
  SEARCH_FLOW_DEFAULT,
  SEARCH_HAS_IMAGES_DEFAULT,
  SEARCH_KEYWORDS_DEFAULT,
  SEARCH_LOCATION_DEFAULT,
  SEARCH_MAX_PRICE_DEFAULT,
  SEARCH_MIN_BATHROOMS_DEFAULT,
  SEARCH_MIN_CAR_SPACES_DEFAULT,
  SEARCH_MIN_LAND_SIZE_DEFAULT,
  SEARCH_MIN_PRICE_DEFAULT,
  SEARCH_POLYGONS_DEFAULT,
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM,
  SEARCH_PROPERTY_STATUS_DEFAULT,
  SEARCH_PROPERTY_TYPE_DEFAULT_ITEM,
  SEARCH_SURROUNDING_SUBURBS_DEFAULT,
} from '../defaults';
import { z } from 'zod';
import { decodeBase64OrDefault } from './fromBase64StrOrDefault';
import { SearchPropertyStatus } from '../../types';

export const getPropertyStatusFromUrl = (url: string): SearchPropertyStatus =>
  (queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.propertyStatus ??
    SEARCH_PROPERTY_STATUS_DEFAULT) as SearchPropertyStatus;

export const getPropertyTypesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.propertyTypes ??
  SEARCH_PROPERTY_TYPE_DEFAULT_ITEM;

export const getMinBathroomsFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.minBathrooms ??
  SEARCH_MIN_BATHROOMS_DEFAULT;

export const getMinBedroomsFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.minBedrooms ??
  SEARCH_MIN_BATHROOMS_DEFAULT;

export const getMinLandSizeFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.minLandSize ??
  SEARCH_MIN_LAND_SIZE_DEFAULT;

export const getIndoorFeaturesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.indoorFeatures ??
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM;

export const getOutdoorFeaturesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.outdoorFeatures ??
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM;

export const getEcoFriendlyFeaturesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.ecoFriendlyFeatures ??
  SEARCH_PROPERTY_FEATURES_DEFAULT_ITEM;

export const getMinCarSpacesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.minCarSpaces ??
  SEARCH_MIN_CAR_SPACES_DEFAULT;

export const getKeywordsFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.keywords ?? SEARCH_KEYWORDS_DEFAULT;

export const getExcludeUnderOfferFromUrl = (url: string): string => {
  const excludeUnderOffer =
    queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.excludeUnderOffer ?? '';
  if (/(true|false)/.test(excludeUnderOffer)) return excludeUnderOffer;
  return SEARCH_EXCLUDE_UNDER_OFFER_DEFAULT;
};

export const getHasImagesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.hasImages ?? SEARCH_HAS_IMAGES_DEFAULT;

export const getExcludeDepositTakenFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url)?.excludeDepositTaken ??
  SEARCH_EXCLUDE_DEPOSIT_TAKEN_DEFAULT;

export const getLocationsFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).locations ?? SEARCH_LOCATION_DEFAULT;

export const getBoundingBoxFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).boundingBox ??
  SEARCH_BOUNDING_BOX_DEFAULT;

export const getPolygonsFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).polygons ?? SEARCH_POLYGONS_DEFAULT;

export const getSurroundingSuburbsUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).surroundingSuburbs ??
  SEARCH_SURROUNDING_SUBURBS_DEFAULT;

export const getMinPriceFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).minPrice ?? SEARCH_MIN_PRICE_DEFAULT;

export const getMaxPriceFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).maxPrice ?? SEARCH_MAX_PRICE_DEFAULT;

export const getCommutesFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).commutes ?? SEARCH_COMMUTES_DEFAULT;

export const getSearchFlowFromUrl = (url: string): string =>
  queryStringParamsCache.extractQueryAsParamsFromUrl(url).flow ?? SEARCH_FLOW_DEFAULT;

const findSimilarSchema = z.object({
  imageId: z.string(),
  imageName: z.string().optional(),
});

export const getFindSimilarImageFromUrl = (
  url: string,
): z.Infer<typeof findSimilarSchema> | null => {
  const findSimilar = queryStringParamsCache.extractQueryAsParamsFromUrl(url).findSimilar ?? null;
  if (!findSimilar) return null;

  try {
    const parsed = findSimilarSchema.safeParse(JSON.parse(decodeBase64OrDefault(findSimilar)));
    if (!parsed.success) return null;
    return parsed.data;
  } catch {
    return null;
  }
};
